// Employees service - for managing non-driver employees
import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    serverTimestamp,
    orderBy
} from 'firebase/firestore';
import { db } from '../../firebase';

export const employeesService = {
    /**
     * Generate employee ID
     */
    async generateEmployeeId(companyId) {
        try {
            const employeesRef = collection(db, 'employees');
            
            // First try with orderBy, if it fails (no index), use simple query
            let snapshot;
            try {
                const q = query(
                    employeesRef, 
                    where('companyId', '==', companyId),
                    orderBy('employeeNumber', 'desc')
                );
                snapshot = await getDocs(q);
            } catch (indexError) {
                console.warn('OrderBy query failed, using simple query:', indexError);
                // Fallback to simple query without orderBy
                const q = query(employeesRef, where('companyId', '==', companyId));
                snapshot = await getDocs(q);
            }
            
            let nextNumber = 1;
            if (!snapshot.empty) {
                // Find the highest employee number manually
                let maxNumber = 0;
                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.employeeNumber && data.employeeNumber > maxNumber) {
                        maxNumber = data.employeeNumber;
                    }
                });
                nextNumber = maxNumber + 1;
            }
            
            return `EMP${String(nextNumber).padStart(4, '0')}`;
        } catch (error) {
            console.error('Error generating employee ID:', error);
            // Fallback to timestamp-based ID
            const timestamp = Date.now().toString().slice(-6);
            return `EMP${timestamp}`;
        }
    },

    /**
     * Get all employees for a company
     */
    async getEmployees(companyId) {
        try {
            const employeesRef = collection(db, 'employees');
            const q = query(employeesRef, where('companyId', '==', companyId));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error('Error getting employees:', error);
            throw error;
        }
    },

    /**
     * Get employee by ID
     */
    async getEmployee(employeeId) {
        try {
            const employeeRef = doc(db, 'employees', employeeId);
            const snapshot = await getDoc(employeeRef);
            if (snapshot.exists()) {
                return { id: snapshot.id, ...snapshot.data() };
            }
            return null;
        } catch (error) {
            console.error('Error getting employee:', error);
            throw error;
        }
    },

    /**
     * Add new employee
     */
    async addEmployee(employeeData) {
        try {
            const employeeId = await this.generateEmployeeId(employeeData.companyId);
            
            // Get the next employee number
            const employeesRef = collection(db, 'employees');
            
            let snapshot;
            try {
                const q = query(
                    employeesRef, 
                    where('companyId', '==', employeeData.companyId),
                    orderBy('employeeNumber', 'desc')
                );
                snapshot = await getDocs(q);
            } catch (indexError) {
                console.warn('OrderBy query failed, using simple query:', indexError);
                const q = query(employeesRef, where('companyId', '==', employeeData.companyId));
                snapshot = await getDocs(q);
            }
            
            let employeeNumber = 1;
            if (!snapshot.empty) {
                // Find the highest employee number manually
                let maxNumber = 0;
                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.employeeNumber && data.employeeNumber > maxNumber) {
                        maxNumber = data.employeeNumber;
                    }
                });
                employeeNumber = maxNumber + 1;
            }

            const newEmployee = {
                ...employeeData,
                employeeId,
                employeeNumber,
                type: 'employee',
                status: employeeData.status || 'active',
                salary: employeeData.salary || 0,
                commissionRate: employeeData.commissionRate || 0,
                commissionEligible: employeeData.commissionEligible || false,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            const docRef = await addDoc(employeesRef, newEmployee);
            return docRef.id;
        } catch (error) {
            console.error('Error adding employee:', error);
            throw error;
        }
    },

    /**
     * Update employee
     */
    async updateEmployee(employeeId, employeeData) {
        try {
            const employeeRef = doc(db, 'employees', employeeId);
            await updateDoc(employeeRef, {
                ...employeeData,
                updatedAt: serverTimestamp()
            });
            return true;
        } catch (error) {
            console.error('Error updating employee:', error);
            throw error;
        }
    },

    /**
     * Delete employee
     */
    async deleteEmployee(employeeId) {
        try {
            const employeeRef = doc(db, 'employees', employeeId);
            await deleteDoc(employeeRef);
            return true;
        } catch (error) {
            console.error('Error deleting employee:', error);
            throw error;
        }
    },

    /**
     * Update employee salary and commission settings
     */
    async updateEmployeeSalary(employeeId, salaryData, updatedBy) {
        try {
            console.log('🔄 Updating employee salary in employees service:', { employeeId, salaryData });
            
            const employeeRef = doc(db, 'employees', employeeId);
            
            const updateData = {
                salary: parseFloat(salaryData.salary) || 0,
                commissionRate: parseFloat(salaryData.commissionRate) || 0,
                commissionEligible: Boolean(salaryData.commissionEligible),
                status: salaryData.status || 'active',
                updatedBy,
                updatedAt: serverTimestamp()
            };
            
            console.log('💾 Update data:', updateData);
            
            await updateDoc(employeeRef, updateData);
            console.log('✅ Employee document updated successfully');

            return true;
        } catch (error) {
            console.error('❌ Error updating employee salary in employees service:', error);
            throw error;
        }
    }
};